'use client'

import { createClient } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Link from 'next/link'
import { useState } from 'react'
import { Mail } from 'lucide-react'
import posthog from 'posthog-js'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const supabase = createClient()
    setIsLoading(true)
    setError(null)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        // Environment-correct by construction (localhost / dev.ponelat.com /
        // app.stridenotes.co.za). NOTE: the URL must be on the Supabase
        // project's redirect allow-list, or GoTrue swaps in the Site URL.
        // /auth/confirm settles the recovery session, then forwards to
        // update-password.
        redirectTo: `${window.location.origin}/auth/confirm?next=/auth/update-password`,
      })
      if (error) throw error
      posthog.capture('password_reset_requested')
      setSent(true)
    } catch (error: unknown) {
      posthog.captureException(error)
      setError(
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center bg-background p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-center">
            <img src="/logo.svg" alt="StrideNotes" className="h-24 w-auto" />
          </div>
          <Card>
            {sent ? (
              <>
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Check your email</CardTitle>
                  <CardDescription>
                    We&apos;ve sent you a password reset link
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <p className="text-sm text-muted-foreground text-center">
                    If an account exists for that address, you&apos;ll get an
                    email shortly. Open the link to choose a new password.
                  </p>
                  <Button asChild className="w-full" variant="outline">
                    <Link href="/auth/login">Back to sign in</Link>
                  </Button>
                </CardContent>
              </>
            ) : (
              <>
                <CardHeader>
                  <CardTitle className="text-2xl">Forgot password</CardTitle>
                  <CardDescription>
                    Enter your email and we&apos;ll send a reset link
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit}>
                    <div className="flex flex-col gap-6">
                      <div className="grid gap-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="name@example.com"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      {error && (
                        <p className="text-sm text-destructive">{error}</p>
                      )}
                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? 'Sending...' : 'Send reset link'}
                      </Button>
                    </div>
                    <div className="mt-4 text-center text-sm">
                      <Link
                        href="/auth/login"
                        className="underline underline-offset-4 text-primary"
                      >
                        Back to sign in
                      </Link>
                    </div>
                  </form>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
